import { useState, useCallback, useEffect } from 'react';
import superjson from 'superjson';
import {
  n8nApi,
  N8nWorkflow,
  N8nExecution,
  N8nCredentials,
  N8nCredential,
  createEmptyWorkflow,
  createWebhookWorkflow,
} from './n8nApi';

interface UseN8nApiOptions {
  autoConnect?: boolean;
}

interface N8nState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  workflows: N8nWorkflow[];
  executions: N8nExecution[];
  credentials: N8nCredential[];
  selectedWorkflow: N8nWorkflow | null;
  isLoading: boolean;
  instanceUrl: string | null;
}

export function useN8nApi({ autoConnect = true }: UseN8nApiOptions = {}) {
  const [state, setState] = useState<N8nState>({
    isConnected: false,
    isConnecting: false,
    error: null,
    workflows: [],
    executions: [],
    credentials: [],
    selectedWorkflow: null,
    isLoading: false,
    instanceUrl: null,
  });

  // Load saved credentials from API
  const loadSavedCredentials = useCallback(async (): Promise<N8nCredentials | null> => {
    try {
      const res = await fetch('/_api/integrations/n8n.credentials');
      if (!res.ok) return null;
      const data = await res.json();
      return data.credentials || null;
    } catch (e) {
      console.error('Failed to load n8n credentials:', e);
      return null;
    }
  }, []);

  // Save credentials to API
  const saveCredentials = useCallback(async (creds: N8nCredentials & { instanceName?: string }) => {
    try {
      const res = await fetch('/_api/integrations/n8n', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify(creds),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save credentials');
      }
    } catch (e) {
      console.error('Failed to save n8n credentials:', e);
      throw e;
    }
  }, []);

  // Clear saved credentials via API
  const clearSavedCredentials = useCallback(async () => {
    try {
      await fetch('/_api/integrations/n8n', { method: 'DELETE' });
    } catch (e) {
      console.error('Failed to clear n8n credentials:', e);
    }
  }, []);

  // Connect to n8n instance
  const connect = useCallback(
    async (credentials: N8nCredentials & { instanceName?: string }) => {
      setState((prev) => ({ ...prev, isConnecting: true, error: null }));

      try {
        n8nApi.setCredentials(credentials);
        const isHealthy = await n8nApi.healthCheck();

        if (!isHealthy) {
          throw new Error('Could not connect to n8n instance. Check URL and API key.');
        }

        // Save credentials on successful connection
        await saveCredentials(credentials);

        // Fetch initial data
        const [workflowsRes, credsRes] = await Promise.all([
          n8nApi.listWorkflows(),
          n8nApi.listCredentials(),
        ]);

        setState((prev) => ({
          ...prev,
          isConnected: true,
          isConnecting: false,
          workflows: workflowsRes.data || [],
          credentials: credsRes.data || [],
          instanceUrl: credentials.instanceUrl,
          error: null,
        }));
      } catch (error) {
        n8nApi.clearCredentials();
        setState((prev) => ({
          ...prev,
          isConnected: false,
          isConnecting: false,
          error: error instanceof Error ? error.message : 'Connection failed',
        }));
      }
    },
    [saveCredentials],
  );

  // Disconnect from n8n
  const disconnect = useCallback(async () => {
    n8nApi.clearCredentials();
    await clearSavedCredentials();
    setState({
      isConnected: false,
      isConnecting: false,
      error: null,
      workflows: [],
      executions: [],
      credentials: [],
      selectedWorkflow: null,
      isLoading: false,
      instanceUrl: null,
    });
  }, [clearSavedCredentials]);

  // Refresh workflows list
  const refreshWorkflows = useCallback(async () => {
    if (!state.isConnected) return;

    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const res = await n8nApi.listWorkflows();
      setState((prev) => ({
        ...prev,
        workflows: res.data || [],
        isLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch workflows',
      }));
    }
  }, [state.isConnected]);

  // Select a workflow and load its full data
  const selectWorkflow = useCallback(async (id: string | null) => {
    if (!id) {
      setState((prev) => ({ ...prev, selectedWorkflow: null, executions: [] }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const [workflow, executionsRes] = await Promise.all([
        n8nApi.getWorkflow(id),
        n8nApi.listExecutions({ workflowId: id, limit: 20 }),
      ]);

      setState((prev) => ({
        ...prev,
        selectedWorkflow: workflow,
        executions: executionsRes.data || [],
        isLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load workflow',
      }));
    }
  }, []);

  // Create a new workflow
  const createWorkflow = useCallback(
    async (name: string, type: 'empty' | 'webhook' = 'empty', webhookPath?: string) => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const workflowData =
          type === 'webhook' && webhookPath
            ? createWebhookWorkflow(name, webhookPath)
            : createEmptyWorkflow(name);

        const created = await n8nApi.createWorkflow(workflowData);

        setState((prev) => ({
          ...prev,
          workflows: [...prev.workflows, created],
          selectedWorkflow: created,
          isLoading: false,
        }));

        return created;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to create workflow',
        }));
        throw error;
      }
    },
    [],
  );

  // Update a workflow
  const updateWorkflow = useCallback(async (id: string, data: Partial<N8nWorkflow>) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const updated = await n8nApi.updateWorkflow(id, data);

      setState((prev) => ({
        ...prev,
        workflows: prev.workflows.map((w) => (w.id === id ? updated : w)),
        selectedWorkflow: prev.selectedWorkflow?.id === id ? updated : prev.selectedWorkflow,
        isLoading: false,
      }));

      return updated;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to update workflow',
      }));
      throw error;
    }
  }, []);

  // Delete a workflow
  const deleteWorkflow = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      await n8nApi.deleteWorkflow(id);

      setState((prev) => ({
        ...prev,
        workflows: prev.workflows.filter((w) => w.id !== id),
        selectedWorkflow: prev.selectedWorkflow?.id === id ? null : prev.selectedWorkflow,
        isLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to delete workflow',
      }));
      throw error;
    }
  }, []);

  // Activate/deactivate a workflow
  const toggleWorkflowActive = useCallback(async (id: string, active: boolean) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const updated = active
        ? await n8nApi.activateWorkflow(id)
        : await n8nApi.deactivateWorkflow(id);

      setState((prev) => ({
        ...prev,
        workflows: prev.workflows.map((w) => (w.id === id ? { ...w, active } : w)),
        selectedWorkflow:
          prev.selectedWorkflow?.id === id
            ? { ...prev.selectedWorkflow, active }
            : prev.selectedWorkflow,
        isLoading: false,
      }));

      return updated;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to toggle workflow',
      }));
      throw error;
    }
  }, []);

  // Execute a workflow manually
  const executeWorkflow = useCallback(async (id: string, data?: Record<string, unknown>) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const execution = await n8nApi.executeWorkflow(id, data);

      setState((prev) => ({
        ...prev,
        executions: [execution, ...prev.executions],
        isLoading: false,
      }));

      return execution;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to execute workflow',
      }));
      throw error;
    }
  }, []);

  // Refresh executions for selected workflow
  const refreshExecutions = useCallback(async () => {
    if (!state.selectedWorkflow) return;

    try {
      const res = await n8nApi.listExecutions({
        workflowId: state.selectedWorkflow.id,
        limit: 20,
      });
      setState((prev) => ({
        ...prev,
        executions: res.data || [],
      }));
    } catch (error) {
      console.error('Failed to refresh executions:', error);
    }
  }, [state.selectedWorkflow]);

  // Clear error
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Auto-connect on mount if credentials are saved
  useEffect(() => {
    if (autoConnect) {
      loadSavedCredentials().then((saved) => {
        if (saved) {
          connect(saved);
        }
      });
    }
  }, [autoConnect]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    ...state,
    connect,
    disconnect,
    refreshWorkflows,
    selectWorkflow,
    createWorkflow,
    updateWorkflow,
    deleteWorkflow,
    toggleWorkflowActive,
    executeWorkflow,
    refreshExecutions,
    clearError,
    loadSavedCredentials,
  };
}
